import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e579f282"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  class: "advert-specific-section"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "image-title" }, "Image", -1))
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "image-navigator" }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { ValidatedEvent } from "devextreme/ui/validator";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import { MarketingEmailItemsValidation } from "acquaint-api/validation/MarketingEmailItemsValidation";
import { MarketingEmailItems } from "acquaint-api/models/MarketingEmailItems";
import AcquaintTextArea from "../ui/inputs/acquaint-text-area.vue";
import { CodeType } from "acquaint-api/constants/CodeType";
import SystemCodeSingleSelect from "../ui/system-codes/system-code-single-select.vue";
import { MarketingEmailItemType } from "@/enums/emails/MarketingEmailItemType";


export default /*#__PURE__*/_defineComponent({
  __name: 'new-edit-marketing-email-items-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the marketing email.
   * @type {number}
   * @required
   */
  configurationId: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when an existing template configuration  has been successfully edited.
   * This notifies the parent component to refresh or update the template configuration details.
   */
  "configurationItemEdited",
  /**
   * Emitted when a new template configuration has been successfully created.
   * This notifies the parent component to handle the creation of a new template configuration.
   */
  "configurationItemCreated",
],
  setup(__props, { emit }) {

const props = __props;





const originalItemData = ref<MarketingEmailItems>();
const itemData = ref<MarketingEmailItems>();
const lockedByAnotherUser = ref(false);
const keepingModalOpening = ref(false);
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const displayCloseConfirmationPrompt = ref(false);

const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const lockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/marketingemailitems/${props.configurationId}/lock`,
      { lock: true, extraInfo: "" },
    );
  } catch (err) {
    lockedByAnotherUser.value = true;
    displayErrorMessage(
      "These details are already being edited by another user.",
    );
  }
};

const unLockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/marketingemailitems/${props.configurationId}/lock`,
      { lock: false, extraInfo: "" },
    );
  } catch (err) {
    displayErrorMessage("We were unable to unlock these details.");
  }
};

const fetchItemData = async () => {
  try {
    if (props.configurationId == 0) {
      originalItemData.value =
        await acquaintApiClient.httpGet<MarketingEmailItems>(
          `database/marketingemailitems/defaultmodel`,
        );
    } else {
      originalItemData.value =
        await acquaintApiClient.httpGet<MarketingEmailItems>(
          `database/marketingemailitems/${props.configurationId}`,
        );
    }
  } catch (err) {
    itemData.value = undefined;
    originalItemData.value = undefined;
    if (props.configurationId == 0) {
      displayErrorMessage("The selected details could not be fetched");
    } else {
      displayErrorMessage("We could not fetch the default data");
    }
  }
};

// We need to add 1 to the image value to get the correct index as the url uses base 1 rahter than base 0
const imageURL = computed(() => {
  const imageIndex = itemData.value ? itemData.value.image + 1 : 0;
  return `https://webutils.acquaintcrm.co.uk/images/marketingemails/image${imageIndex}.jpg`;
});

const displayPreviousImage = () => {
  if (!itemData.value) {
    return;
  }

  // If we are viewing the first image, we need to display the last image after pressing the back button
  if (itemData.value.image == 0) {
    itemData.value.image = 94;
    return;
  }

  itemData.value.image--;
};

const displayNextImage = () => {
  if (!itemData.value) {
    return;
  }

  // If we are viewing the last image, we need to display the first image after pressing the next button
  if (itemData.value.image == 94) {
    itemData.value.image = 0;
    return;
  }

  itemData.value.image++;
};

watch(
  () => itemData.value,
  () => {
    displayCloseConfirmationPrompt.value =
      JSON.stringify(itemData.value) != JSON.stringify(originalItemData.value);
  },
  { deep: true },
);

watch(
  () => showModal.value,
  async () => {
    if (keepingModalOpening.value || !showModal.value) {
      return;
    }

    lockedByAnotherUser.value = false;
    displayCloseConfirmationPrompt.value = false;

    await fetchItemData();
    if (originalItemData.value) {
      itemData.value = JSON.parse(JSON.stringify(originalItemData.value));
    }

    if (props.configurationId == 0) {
      return;
    }

    await lockConfigurationItem();
  },
);

// We need to ensure that the image is set to the first image when it's displayed after being hidden
watch(
  () => itemData.value?.type,
  (value, preValue) => {
    if (
      itemData.value &&
      value == MarketingEmailItemType.Advert &&
      preValue == MarketingEmailItemType.MenuItem
    ) {
      itemData.value.image = 0;
    }
  },
);

const saveDetails = async (e: any) => {
  const validation: ValidatedEvent = e.validationGroup.validate();
  // Don't continue if user has entered invalid data
  if (!validation.isValid) {
    return;
  }

  if (!(await callSaveApi())) {
    return;
  }

  lockedByAnotherUser.value = true;
  displayCloseConfirmationPrompt.value = false;

  if (props.configurationId > 0) {
    await unLockConfigurationItem();
    emit("configurationItemEdited");
  } else {
    emit("configurationItemCreated");
  }

  showModal.value = false;
};

const callSaveApi = async () => {
  try {
    if (props.configurationId == 0) {
      itemData.value = await acquaintApiClient.httpPost<MarketingEmailItems>(
        `database/marketingemailitems`,
        itemData.value,
      );
    } else {
      itemData.value = await acquaintApiClient.httpPut<MarketingEmailItems>(
        `database/marketingemailitems/${props.configurationId}`,
        itemData.value,
      );
    }
    return true;
  } catch (err) {
    if (props.configurationId == 0) {
      displayErrorMessage("A new system item could not be created.");
    } else {
      displayErrorMessage("The selected details could not be edited.");
    }
    return false;
  }
};
const cancelClick = () => {
  showModal.value = false;
};

const keepEnteredInformation = () => {
  keepingModalOpening.value = true;
};

const cancelPromptDenied = () => {
  keepingModalOpening.value = true;
};

const modalClosed = async () => {
  if (props.configurationId > 0) {
    await unLockConfigurationItem();
  }
  keepingModalOpening.value = false;
};

// Because the body field and images can either be hidden or displayed, we need to change the modal height on large devices depending on whether they are shown or not.
const modalHeight = computed(() => {
  return itemData.value?.type == MarketingEmailItemType.Advert
    ? "560px"
    : "300px";
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(modalBase, {
    title: "Marketing Email Item",
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showModal).value = $event)),
    showCloseButton: true,
    showCloseConfirmationPrompt: displayCloseConfirmationPrompt.value,
    height: modalHeight.value,
    onCancelConfirmationModal: keepEnteredInformation,
    onModalClosed: modalClosed,
    onCancelPromptDenied: cancelPromptDenied
  }, {
    content: _withCtx(() => [
      (itemData.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
            _createVNode(AcquaintTextbox, {
              fieldValidation: _unref(MarketingEmailItemsValidation).Description,
              label: "Description",
              modelValue: itemData.value.description,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemData.value.description) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "MarketingEmailItemsValidationGroup"
            }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
            _createVNode(SystemCodeSingleSelect, {
              "code-type": _unref(CodeType).MarketingEmailItemType,
              fieldValidation: _unref(MarketingEmailItemsValidation).Type,
              label: "Type",
              modelValue: itemData.value.type,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((itemData.value.type) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "MarketingEmailItemsValidationGroup"
            }, null, 8, ["code-type", "fieldValidation", "modelValue", "readOnly"]),
            _createVNode(AcquaintTextbox, {
              fieldValidation: _unref(MarketingEmailItemsValidation).URL,
              label: "URL",
              modelValue: itemData.value.url,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itemData.value.url) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "MarketingEmailItemsValidationGroup"
            }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
            (itemData.value.type == _unref(MarketingEmailItemType).Advert)
              ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
                  _createVNode(AcquaintTextArea, {
                    fieldValidation: _unref(MarketingEmailItemsValidation).Body,
                    label: "Body",
                    modelValue: itemData.value.body,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemData.value.body) = $event)),
                    readOnly: lockedByAnotherUser.value,
                    validationGroup: "MarketingEmailItemsValidationGroup"
                  }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
                  _hoisted_3,
                  _createElementVNode("img", {
                    src: imageURL.value,
                    height: "150",
                    width: "200"
                  }, null, 8, _hoisted_4),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("i", {
                      class: "fas fa-chevron-left",
                      onClick: displayPreviousImage
                    }),
                    _createElementVNode("span", null, _toDisplayString(itemData.value.image + 1) + "/" + _toDisplayString(95), 1),
                    _createElementVNode("i", {
                      class: "fas fa-chevron-right",
                      onClick: displayNextImage
                    })
                  ])
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          disabled: lockedByAnotherUser.value,
          onClick: (e) => {
            saveDetails(e);
          },
          validationGroup: 'MarketingEmailItemsValidationGroup',
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "showCloseConfirmationPrompt", "height"]))
}
}

})